import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Camera, X, FileSpreadsheet, CheckCircle2, XCircle, Undo2,
  AlertTriangle, Scale, ScanBarcode,
} from "lucide-react";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StockCountSession() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [scanInput, setScanInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null); // { name, plu, counted, expected, delta }
  const [cameraOpen, setCameraOpen] = useState(false);
  const [qtyPrompt, setQtyPrompt] = useState(null); // {product, mode: 'scan'|'edit'}
  const [qtyInput, setQtyInput] = useState("");
  const qtyPromptOpenRef = useRef(false);
  const [unknownBarcode, setUnknownBarcode] = useState(null);
  // Track barcodes the user dismissed in THIS tab so a stale poll response
  // (or an intermittent 2s window before the backend clears the flag)
  // doesn't re-open the modal for the same code repeatedly.
  const dismissedRef = useRef(new Set());
  const [closing, setClosing] = useState(false);
  const scanInputRef = useRef(null);

  const isReadOnly = session && session.status !== "active";

  // ── Session polling — supports shared PC/phone view ──────────────────
  const loadSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stock-count/sessions/${code}`);
      setSession(res.data);
      const pending = res.data?.pending_unknown;
      if (pending && !dismissedRef.current.has(pending)) {
        setUnknownBarcode(pending);
      }
      // Shared qty-confirmation prompt — the PC scans; the phone gets
      // the modal. Whichever device answers first wins.
      const promptPayload = res.data?.pending_prompt;
      if (promptPayload && !qtyPromptOpenRef.current) {
        qtyPromptOpenRef.current = true;
        setQtyPrompt({ product: promptPayload.product, prevScan: promptPayload.query });
        setQtyInput(
          promptPayload.product?.expected_qty != null
            ? String(promptPayload.product.expected_qty)
            : ""
        );
      } else if (!promptPayload && qtyPromptOpenRef.current) {
        // Another device already answered — close this modal locally.
        qtyPromptOpenRef.current = false;
        setQtyPrompt(null);
      }
    } catch {
      toast.error("Session not found");
      navigate("/stock-count");
    }
  }, [code, navigate]);

  useEffect(() => {
    loadSession();
    const t = setInterval(loadSession, 2000);
    return () => clearInterval(t);
  }, [loadSession]);

  // Keep focus on the scan input so USB scanners just work.
  useEffect(() => {
    if (!cameraOpen && !qtyPrompt && !unknownBarcode && !isReadOnly) {
      scanInputRef.current?.focus();
    }
  }, [cameraOpen, qtyPrompt, unknownBarcode, isReadOnly, session?.status]);

  // ── Scan handler ─────────────────────────────────────────────────────
  const submitScan = async (query, extraQty = null) => {
    if (!query || scanning) return;
    setScanning(true);
    try {
      const body = { query: String(query).trim() };
      if (extraQty !== null) body.qty = Number(extraQty);
      const res = await axios.post(`${API}/stock-count/sessions/${code}/scan`, body);
      if (res.data.need_qty) {
        // Backend has broadcast the prompt to the shared session; the
        // poll on THIS device (and the phone's) will pick it up and
        // open the modal. Nothing more to do here — just clear input.
        setScanInput("");
        loadSession();
        return;
      }
      const p = res.data.product;
      setLastScan({
        name: p.name,
        plu: p.plu_code,
        counted: p.counted_qty,
        expected: p.expected_qty,
        unit: p.unit,
        location_reassigned: p.location_reassigned,
        location_from: p.location_from,
        location_to: p.location_to,
      });
      if (p.location_reassigned) {
        toast.success(`Location updated: ${p.location_from || "—"} → ${p.location_to}`);
      }
      setScanInput("");
      loadSession();
    } catch (err) {
      if (err?.response?.status === 404 && err?.response?.data?.detail?.code === "UNKNOWN_BARCODE") {
        setUnknownBarcode(err.response.data.detail.barcode);
      } else {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
      setScanInput("");
    } finally {
      setScanning(false);
    }
  };

  const onScanKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitScan(scanInput);
    }
  };

  // ── Camera scanner ───────────────────────────────────────────────────
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    try { readerRef.current && readerRef.current.reset(); } catch { /* noop */ }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!cameraOpen) { stopCamera(); return; }
    let cancelled = false;
    (async () => {
      try {
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E, BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
          BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
        ]);
        const reader = new BrowserMultiFormatReader(hints);
        readerRef.current = reader;

        // Prefer rear camera on phones.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        reader.decodeFromVideoElementContinuously(videoRef.current, (result, err) => {
          if (cancelled) return;
          if (result) {
            const text = result.getText();
            if (text) {
              setCameraOpen(false);
              submitScan(text);
              // brief buzz for phones that support it
              try { navigator.vibrate && navigator.vibrate(60); } catch { /* noop */ }
            }
          }
        });
      } catch (err) {
        console.error(err);
        toast.error("Couldn't open camera");
        setCameraOpen(false);
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, [cameraOpen]);

  // ── Qty submit ───────────────────────────────────────────────────────
  const submitQty = async () => {
    const val = parseFloat(qtyInput);
    if (!Number.isFinite(val) || val < 0) { toast.error("Enter a valid qty"); return; }
    await submitScan(qtyPrompt.prevScan, val);
    setQtyPrompt(null);
  };

  // ── Manual variance override ─────────────────────────────────────────
  const [editing, setEditing] = useState(null); // {product_id, expected, counted}
  const [editQty, setEditQty] = useState("");
  const saveEdit = async () => {
    const v = parseFloat(editQty);
    if (!Number.isFinite(v) || v < 0) { toast.error("Enter a valid qty"); return; }
    try {
      await axios.put(`${API}/stock-count/sessions/${code}/items/${editing.product_id}`,
        { counted_qty: v, note: "Manual override" });
      toast.success("Updated");
      setEditing(null);
      loadSession();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // ── Unknown barcode → attach to Stock Master flow ────────────────────
  const [attaching, setAttaching] = useState(null); // product object
  const [attachSearch, setAttachSearch] = useState("");
  const [attachResults, setAttachResults] = useState([]);
  const searchProducts = async (q) => {
    setAttachSearch(q);
    if (!q || q.length < 2) { setAttachResults([]); return; }
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(q)}`);
      setAttachResults(res.data?.products || res.data || []);
    } catch { /* ignore */ }
  };
  const dismissUnknown = async () => {
    if (unknownBarcode) dismissedRef.current.add(unknownBarcode);
    try {
      await axios.post(`${API}/stock-count/sessions/${code}/dismiss-unknown`);
    } catch { /* ignore — local dismiss already blocks re-open */ }
    setUnknownBarcode(null); setAttaching(null); setAttachSearch(""); setAttachResults([]);
  };

  const attachBarcode = async () => {
    if (!attaching || !unknownBarcode) return;
    const bc = unknownBarcode;
    try {
      await axios.put(`${API}/products/${attaching.id}`, { barcode: bc });
      toast.success("Barcode attached — scan again to count");
      // Clear session flag so the poll doesn't re-open the modal.
      await axios.post(`${API}/stock-count/sessions/${code}/dismiss-unknown`).catch(() => {});
      dismissedRef.current.add(bc);
      // Re-scan now that the barcode maps to a product.
      await axios.post(`${API}/stock-count/sessions/${code}/scan`, { query: bc }).catch(() => {});
      setUnknownBarcode(null); setAttaching(null); setAttachSearch(""); setAttachResults([]);
      loadSession();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't attach barcode");
    }
  };

  // ── Close session ────────────────────────────────────────────────────
  const closeSession = async (apply) => {
    const msg = apply
      ? `Close and APPLY variances?\n\nThis will update the current_stock of every counted product to your counted qty. Untouched products are unchanged. This action is written to the audit log.`
      : `Abandon this session? No stock will be changed. The count history is kept for reference.`;
    if (!window.confirm(msg)) return;
    setClosing(true);
    try {
      const res = await axios.post(`${API}/stock-count/sessions/${code}/close`,
        { apply_adjustments: apply });
      toast.success(apply
        ? `${res.data.applied} adjustments applied`
        : "Session abandoned");
      loadSession();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't close");
    } finally {
      setClosing(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (!session) return <div className="p-8 text-slate-500">Loading session…</div>;

  const counted = session.counted_items || {};
  // Sort: most-recently-scanned first.
  const scannedItems = Object.values(counted)
    .filter((r) => (r.scans_count || 0) > 0 || r.overridden_at)
    .sort((a, b) => String(b.last_scan_at || b.overridden_at || "").localeCompare(String(a.last_scan_at || a.overridden_at || "")));
  const progress = session.progress || { total_products: 0, counted_products: 0, counted_scans: 0 };
  const pct = progress.total_products > 0
    ? Math.min(100, Math.round(progress.counted_products / progress.total_products * 100))
    : 0;

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/stock-count" className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-3 w-3" /> Sessions
        </Link>
        <Badge
          variant={session.status === "active" ? "secondary" : "outline"}
          className={session.status === "active" ? "bg-emerald-100 text-emerald-800" : ""}
          data-testid="session-status-badge"
        >
          {session.status}
        </Badge>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base flex-wrap gap-2">
            <span>{session.name}</span>
            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">Code: {session.code}</span>
          </CardTitle>
          <p className="text-xs text-slate-500">
            Scope: {session.scope?.type}{session.scope?.value ? ` = ${session.scope.value}` : ""} · {session.blind ? "Blind" : "Open"}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Progress */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600">Progress</span>
              <span className="font-mono">
                {progress.counted_products}/{progress.total_products} products · {progress.counted_scans} scans
              </span>
            </div>
            <Progress value={pct} data-testid="progress-bar" />
          </div>

          {/* Scan input + camera */}
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  ref={scanInputRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={onScanKey}
                  placeholder="Scan / type barcode or PLU, press Enter"
                  className="pl-9 font-mono"
                  autoFocus
                  data-testid="scan-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setCameraOpen(true)}
                className="whitespace-nowrap"
                data-testid="open-camera-btn"
              >
                <Camera className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Camera</span>
              </Button>
            </div>
          )}

          {/* Last scan feedback */}
          {lastScan && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{lastScan.name}</div>
                <div className="text-xs text-slate-500">PLU {lastScan.plu}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-700">
                  {lastScan.counted}{lastScan.unit && lastScan.unit !== "units" ? ` ${lastScan.unit}` : ""}
                </div>
                {lastScan.expected != null && (
                  <div className="text-xs text-slate-500">system: {lastScan.expected}</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Counted items list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Counted items ({scannedItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {scannedItems.length === 0 ? (
            <p className="text-sm text-slate-500">No items counted yet. Start scanning above.</p>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {scannedItems.map((r) => {
                const expected = Number(r.expected_qty || 0);
                const cnt = Number(r.counted_qty || 0);
                const delta = cnt - expected;
                const showBlind = session.blind && session.status === "active";
                return (
                  <div key={r.product_id} className="flex items-center gap-2 border-b py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.name}</div>
                      <div className="text-xs text-slate-500">
                        PLU {r.plu_code} · {r.scans_count || 0} scan{r.scans_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right font-mono text-xs whitespace-nowrap">
                      <div><span className="text-emerald-700 font-bold text-base">{cnt}</span> {r.unit && r.unit !== "units" ? r.unit : ""}</div>
                      {!showBlind && (
                        <div className="text-slate-500">
                          exp {expected}
                          {delta !== 0 && (
                            <span className={delta > 0 ? "text-emerald-600 ml-1" : "text-red-600 ml-1"}>
                              ({delta > 0 ? "+" : ""}{delta})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {!isReadOnly && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditing({ product_id: r.product_id, name: r.name, expected, counted: cnt }); setEditQty(String(cnt)); }}
                        data-testid={`edit-${r.product_id}`}
                        className="px-2"
                      >
                        <Undo2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pb-8">
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const res = await axios.get(`${API}/stock-count/sessions/${code}/xlsx`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = `stock-count-${code}.xlsx`; a.click();
              URL.revokeObjectURL(url);
            } catch { toast.error("Download failed"); }
          }}
          data-testid="xlsx-btn"
        >
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Export XLSX
        </Button>
        {session.status === "active" && (
          <>
            <Button
              variant="outline"
              onClick={() => closeSession(false)}
              disabled={closing}
              data-testid="abandon-btn"
              className="text-slate-600"
            >
              <XCircle className="h-4 w-4 mr-1" /> Abandon
            </Button>
            <Button
              onClick={() => closeSession(true)}
              disabled={closing}
              data-testid="close-apply-btn"
              className="ml-auto"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Close &amp; Apply Adjustments
            </Button>
          </>
        )}
      </div>

      {/* Camera modal */}
      <Dialog open={cameraOpen} onOpenChange={setCameraOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <div className="relative bg-black" style={{ aspectRatio: "3/4" }}>
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="border-2 border-emerald-400/80 w-4/5 h-24 rounded-md" />
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => setCameraOpen(false)}
              data-testid="camera-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="absolute bottom-2 inset-x-0 text-center text-white text-xs px-4">
              Point at a barcode — auto-detected
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Qty prompt */}
      <Dialog open={!!qtyPrompt} onOpenChange={(o) => {
        if (!o) {
          qtyPromptOpenRef.current = false;
          setQtyPrompt(null);
          // Tell the backend so other devices also close.
          axios.post(`${API}/stock-count/sessions/${code}/dismiss-prompt`).catch(() => {});
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {qtyPrompt?.product?.unit && ["kg","g","l","ml","m","cm"].includes((qtyPrompt.product.unit || "").toLowerCase())
                ? <Scale className="h-4 w-4" /> : null}
              Confirm on-hand qty
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="font-medium">{qtyPrompt?.product?.name}</p>
              <p className="text-xs text-slate-500">
                PLU {qtyPrompt?.product?.plu_code} · unit: {qtyPrompt?.product?.unit}
              </p>
            </div>

            {qtyPrompt?.product?.expected_qty != null && (
              <div className="bg-slate-100 rounded p-3 flex items-center justify-between">
                <span className="text-xs uppercase text-slate-500">System expects</span>
                <span className="text-2xl font-bold font-mono">
                  {qtyPrompt.product.expected_qty}
                  {qtyPrompt.product.unit && qtyPrompt.product.unit !== "units" ? ` ${qtyPrompt.product.unit}` : ""}
                </span>
              </div>
            )}

            {qtyPrompt?.product?.will_reassign_location && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
                Location will be updated: <span className="font-mono">{qtyPrompt.product.current_location || "—"}</span>
                {" → "}
                <span className="font-mono">{qtyPrompt.product.session_location}</span>
              </div>
            )}

            <div>
              <Label className="text-xs">Actual counted qty</Label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                autoFocus
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitQty()}
                data-testid="qty-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              qtyPromptOpenRef.current = false;
              setQtyPrompt(null);
              axios.post(`${API}/stock-count/sessions/${code}/dismiss-prompt`).catch(() => {});
            }} className="sm:w-auto">Cancel</Button>
            {qtyPrompt?.product?.expected_qty != null && (
              <Button
                variant="secondary"
                onClick={() => submitScan(qtyPrompt.prevScan, qtyPrompt.product.expected_qty).then(() => setQtyPrompt(null))}
                className="sm:w-auto"
                data-testid="qty-accept-expected-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Correct — move on
              </Button>
            )}
            <Button onClick={submitQty} data-testid="qty-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual override */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Override counted qty</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="font-medium">{editing?.name}</p>
            <Input
              type="number"
              step="0.01"
              inputMode="decimal"
              autoFocus
              value={editQty}
              onChange={(e) => setEditQty(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveEdit()}
              data-testid="edit-qty-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unknown barcode → attach to Stock Master */}
      <Dialog open={!!unknownBarcode} onOpenChange={(o) => !o && dismissUnknown()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Unknown barcode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Barcode <span className="font-mono bg-slate-100 px-1 rounded">{unknownBarcode}</span> isn&apos;t linked to any product. Search for the product to attach it to:
            </p>
            <Input
              value={attachSearch}
              onChange={(e) => searchProducts(e.target.value)}
              placeholder="Type product name or PLU"
              autoFocus
              data-testid="attach-search-input"
            />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {attachResults.slice(0, 20).map((p) => (
                <button
                  key={p.id}
                  onClick={() => setAttaching(p)}
                  className={`w-full text-left p-2 border rounded text-sm hover:bg-slate-50 ${attaching?.id === p.id ? "bg-emerald-50 border-emerald-300" : ""}`}
                  data-testid={`attach-choice-${p.id}`}
                >
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">PLU {p.plu_code} · existing barcode: {p.barcode || "—"}</div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={dismissUnknown} data-testid="attach-cancel-btn">Cancel</Button>
            <Button onClick={attachBarcode} disabled={!attaching} data-testid="attach-confirm-btn">
              Attach &amp; count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
